import { useEffect, useRef, useState } from "react";

/** One place to ask "is this machine asking us to sit still". */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

/**
 * Count a number up to its new value instead of snapping.
 *
 * Used on the KPI tiles. It animates on *change*, not just on mount, so when a
 * poll comes back with a different queue depth the tile visibly moves — which
 * is the difference between a dashboard that looks live and one that looks
 * like a screenshot.
 */
export function useCountUp(value: number, durationMs = 620): number {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion() || from.current === value) {
      from.current = value;
      setShown(value);
      return;
    }
    const start = performance.now();
    const a = from.current;
    const b = value;

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // Same ease-out curve the CSS uses, so motion feels like one system.
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(a + (b - a) * eased));
      if (t < 1) raf.current = requestAnimationFrame(step);
      else from.current = b;
    };
    raf.current = requestAnimationFrame(step);

    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      from.current = value;
    };
  }, [value, durationMs]);

  return shown;
}

/**
 * Flash a section when it is the target of an alert.
 *
 * Alerts carry hrefs like `/stack#storage`. Landing on the page is not
 * enough — on a long page you still have to hunt for the row that is on fire.
 * This scrolls it into view and marks it for a moment.
 */
export function useHashTarget(hash: string): string {
  const [flashing, setFlashing] = useState("");

  useEffect(() => {
    const id = hash.replace(/^#/, "");
    if (!id) return;
    // The section has to exist before we can scroll to it; status renders
    // after its first fetch, so retry across a couple of frames.
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const find = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({
          behavior: prefersReducedMotion() ? "auto" : "smooth",
          block: "start",
        });
        setFlashing(id);
        timer = setTimeout(() => setFlashing(""), 2000);
        return;
      }
      if (tries++ < 20) timer = setTimeout(find, 100);
    };
    find();
    return () => clearTimeout(timer);
  }, [hash]);

  return flashing;
}
